import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const CalendarTab = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [feedUrls, setFeedUrls] = useState(null);
  const [slotModal, setSlotModal] = useState(null); // { date, time }
  const [walkInForm, setWalkInForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    service_name: "",
    notes: "",
  });
  const [savingWalkIn, setSavingWalkIn] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bookings`, authHeaders()).then((r) => setBookings(r.data)).catch((e) => console.error("Failed to load bookings:", e));
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch((e) => console.error("Failed to load services:", e));
    axios.get(`${API}/admin/calendar-feed-url`, authHeaders())
      .then((r) => setFeedUrls(r.data))
      .catch((e) => console.error("Failed to load calendar feed URL:", e));
  }, []);

  const refreshBookings = () =>
    axios.get(`${API}/bookings`, authHeaders()).then((r) => setBookings(r.data)).catch((e) => console.error("Failed to load bookings:", e));

  const monthLabel = viewMonth.toLocaleString("en-US", { month: "long", year: "numeric" });
  const prevMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  const nextMonth = () => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  // Build a 6-week grid starting Sunday
  const gridDays = useMemo(() => {
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startDay = firstOfMonth.getDay(); // 0=Sun
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - startDay);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [viewMonth]);

  const fmt = (d) => {
    // Build YYYY-MM-DD in local time (matches booking.date format saved by backend)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const bookingsByDate = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      if (b.status === "cancelled") return;
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (a.time || "").localeCompare(b.time || ""))
    );
    return map;
  }, [bookings]);

  const TIME_SLOTS = [
    "06:00 AM", "07:00 AM", "08:00 AM", "09:00 AM", "10:00 AM",
    "11:00 AM", "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM",
    "04:00 PM", "05:00 PM", "06:00 PM", "07:00 PM", "08:00 PM",
  ];

  const openWalkInModal = (date, time = "") => {
    setSlotModal({ date: fmt(date), time });
    setWalkInForm({ client_name: "", client_phone: "", client_email: "", service_name: "", notes: "" });
  };

  const submitWalkIn = async () => {
    if (!slotModal) return;
    if (!walkInForm.client_name || !walkInForm.client_phone || !walkInForm.service_name) {
      toast.error("Name, phone and service are required.");
      return;
    }
    if (!slotModal.time) {
      toast.error("Pick a time slot below.");
      return;
    }
    setSavingWalkIn(true);
    try {
      const svc = services.find((s) => s.name === walkInForm.service_name);
      await axios.post(`${API}/bookings`, {
        service_id: svc?.id || "walkin",
        service_name: walkInForm.service_name,
        date: slotModal.date,
        time: slotModal.time,
        client_name: walkInForm.client_name,
        client_email: walkInForm.client_email || "",
        client_phone: walkInForm.client_phone,
        notes: `[Admin walk-in] ${walkInForm.notes || ""}`.trim(),
      }, authHeaders());
      toast.success("Walk-in booked & confirmation emailed.");
      setSlotModal(null);
      refreshBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save walk-in.");
    } finally {
      setSavingWalkIn(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  const today = fmt(new Date());

  return (
    <div className="space-y-6" data-testid="calendar-tab">
      {/* ===== Phone Calendar Sync Panel ===== */}
      <div className="bg-brand-surface border border-brand-border p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
              <CalendarPlus size={18} className="text-brand-gold" /> Sync to Your Phone
            </h3>
            <p className="font-sans text-xs text-brand-text-muted mt-1">
              Subscribe once on your phone. Every new booking shows up in your normal calendar automatically.
            </p>
          </div>
          <button
            onClick={() => setShowInstructions((s) => !s)}
            data-testid="toggle-calendar-instructions"
            className="font-sans text-xs text-brand-gold border border-brand-gold/40 px-3 py-1 hover:bg-brand-gold/10 transition-colors"
          >
            {showInstructions ? "Hide" : "How to add"}
          </button>
        </div>

        {feedUrls && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-brand-bg border border-brand-border px-3 py-2">
              <span className="font-sans text-[10px] text-brand-gold uppercase tracking-wider flex-shrink-0">
                iPhone (Apple Calendar)
              </span>
              <input
                readOnly
                value={feedUrls.webcal_url}
                data-testid="calendar-webcal-url"
                className="flex-1 bg-transparent text-brand-text text-xs font-mono outline-none"
              />
              <button
                onClick={() => copyToClipboard(feedUrls.webcal_url, "webcal:// link")}
                data-testid="copy-webcal-btn"
                className="text-brand-gold hover:text-brand-gold-light"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="flex items-center gap-2 bg-brand-bg border border-brand-border px-3 py-2">
              <span className="font-sans text-[10px] text-brand-gold uppercase tracking-wider flex-shrink-0">
                Google Calendar
              </span>
              <a
                href={feedUrls.google_url}
                target="_blank"
                rel="noreferrer"
                data-testid="google-cal-add-btn"
                className="font-sans text-xs text-brand-gold underline hover:text-brand-gold-light flex-1 truncate"
              >
                Click to add to Google Calendar
              </a>
            </div>
            <div className="flex items-center gap-2 bg-brand-bg border border-brand-border px-3 py-2">
              <span className="font-sans text-[10px] text-brand-text-muted uppercase tracking-wider flex-shrink-0">
                Raw URL
              </span>
              <input
                readOnly
                value={feedUrls.https_url}
                data-testid="calendar-https-url"
                className="flex-1 bg-transparent text-brand-text-muted text-xs font-mono outline-none"
              />
              <button
                onClick={() => copyToClipboard(feedUrls.https_url, "URL")}
                className="text-brand-text-muted hover:text-brand-gold"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}

        {showInstructions && (
          <div className="mt-4 border-t border-brand-border pt-3 space-y-3 text-xs font-sans text-brand-text-muted">
            <div>
              <p className="text-brand-gold font-semibold mb-1">📱 iPhone / iPad (Apple Calendar)</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Open your phone's <strong>Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</strong>.</li>
                <li>Paste the <strong>iPhone</strong> URL above.</li>
                <li>Tap Next, Save. Bookings appear in your stock Calendar app.</li>
              </ol>
            </div>
            <div>
              <p className="text-brand-gold font-semibold mb-1">🟢 Android / Google Calendar</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Tap the <strong>"Click to add to Google Calendar"</strong> link above on a computer.</li>
                <li>Approve the subscription. It syncs to your Android Calendar app automatically.</li>
              </ol>
            </div>
            <p className="text-[10px] text-brand-text-muted/70 italic">
              Updates appear within a few minutes. Keep the URL private — anyone with the link can see your bookings.
            </p>
          </div>
        )}
      </div>

      {/* ===== Month grid with click-to-book ===== */}
      <div className="bg-brand-surface border border-brand-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text">Walk-In Calendar</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              data-testid="calendar-prev-month"
              className="font-sans text-xs text-brand-text-muted border border-brand-border px-2 py-1 hover:text-brand-gold hover:border-brand-gold transition-colors"
            >
              ‹
            </button>
            <span className="font-sans text-sm text-brand-text px-2 min-w-[140px] text-center">{monthLabel}</span>
            <button
              onClick={nextMonth}
              data-testid="calendar-next-month"
              className="font-sans text-xs text-brand-text-muted border border-brand-border px-2 py-1 hover:text-brand-gold hover:border-brand-gold transition-colors"
            >
              ›
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-px bg-brand-border border border-brand-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="bg-brand-surface px-2 py-1 font-sans text-[10px] uppercase tracking-wider text-brand-text-muted text-center">
              {d}
            </div>
          ))}
          {gridDays.map((d, i) => {
            const dateKey = fmt(d);
            const inMonth = d.getMonth() === viewMonth.getMonth();
            const isToday = dateKey === today;
            const dayBookings = bookingsByDate[dateKey] || [];
            return (
              <button
                key={dateKey}
                onClick={() => openWalkInModal(d)}
                data-testid={`calendar-day-${dateKey}`}
                className={`bg-brand-bg min-h-[88px] text-left p-1.5 hover:bg-brand-surface transition-colors group ${inMonth ? "" : "opacity-40"} ${isToday ? "ring-1 ring-brand-gold" : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-sans text-xs ${isToday ? "text-brand-gold font-bold" : "text-brand-text"}`}>{d.getDate()}</span>
                  {dayBookings.length > 0 && (
                    <span className="font-sans text-[9px] text-brand-gold bg-brand-gold/10 px-1.5 py-0.5">{dayBookings.length}</span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayBookings.slice(0, 3).map((b) => (
                    <div
                      key={b.id}
                      className="font-sans text-[10px] text-brand-text-muted truncate"
                      title={`${b.time} ${b.client_name} (${b.service_name})`}
                    >
                      <span className="text-brand-gold">{(b.time || "").replace(":00 ", "")}</span> {b.client_name}
                    </div>
                  ))}
                  {dayBookings.length > 3 && (
                    <div className="font-sans text-[9px] text-brand-text-muted">+{dayBookings.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <p className="font-sans text-[10px] text-brand-text-muted mt-2">
          Tap any day to add a walk-in. Existing bookings are shown inline.
        </p>
      </div>

      {/* ===== Walk-In Modal ===== */}
      {slotModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="walkin-modal">
          <div className="bg-brand-surface border border-brand-border max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-lg text-brand-text">
                Walk-In — {new Date(slotModal.date + "T12:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </h3>
              <button onClick={() => setSlotModal(null)} data-testid="close-walkin-modal" className="text-brand-text-muted hover:text-brand-gold">
                <X size={18} />
              </button>
            </div>

            {/* Time slot picker */}
            <div className="mb-3">
              <p className="font-sans text-xs text-brand-text-muted mb-1.5">Time</p>
              <div className="grid grid-cols-5 gap-1">
                {TIME_SLOTS.map((t) => {
                  const taken = (bookingsByDate[slotModal.date] || []).some((b) => b.time === t);
                  const selected = slotModal.time === t;
                  return (
                    <button
                      key={t}
                      disabled={taken}
                      onClick={() => setSlotModal({ ...slotModal, time: t })}
                      data-testid={`walkin-time-${t}`}
                      className={`font-sans text-[10px] py-1.5 border transition-colors ${
                        selected
                          ? "bg-brand-gold text-black border-brand-gold"
                          : taken
                          ? "border-brand-border text-brand-text-muted/40 line-through cursor-not-allowed"
                          : "border-brand-border text-brand-text hover:border-brand-gold hover:text-brand-gold"
                      }`}
                    >
                      {t.replace(":00 ", "")}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Input
                value={walkInForm.client_name}
                onChange={(e) => setWalkInForm({ ...walkInForm, client_name: e.target.value })}
                placeholder="Client name *"
                data-testid="walkin-name"
                className="bg-brand-bg border-brand-border text-brand-text rounded-none"
              />
              <Input
                value={walkInForm.client_phone}
                onChange={(e) => setWalkInForm({ ...walkInForm, client_phone: e.target.value })}
                placeholder="Phone *"
                data-testid="walkin-phone"
                className="bg-brand-bg border-brand-border text-brand-text rounded-none"
              />
              <Input
                value={walkInForm.client_email}
                onChange={(e) => setWalkInForm({ ...walkInForm, client_email: e.target.value })}
                placeholder="Email (optional, for confirmation)"
                data-testid="walkin-email"
                className="bg-brand-bg border-brand-border text-brand-text rounded-none"
              />
              <Select
                value={walkInForm.service_name}
                onValueChange={(v) => setWalkInForm({ ...walkInForm, service_name: v })}
              >
                <SelectTrigger data-testid="walkin-service" className="bg-brand-bg border-brand-border text-brand-text rounded-none">
                  <SelectValue placeholder="Service *" />
                </SelectTrigger>
                <SelectContent className="bg-brand-surface border-brand-border max-h-60">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.name} className="text-brand-text">
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={walkInForm.notes}
                onChange={(e) => setWalkInForm({ ...walkInForm, notes: e.target.value })}
                placeholder="Notes (optional)"
                data-testid="walkin-notes"
                className="bg-brand-bg border-brand-border text-brand-text rounded-none min-h-[60px]"
              />
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setSlotModal(null)}
                data-testid="walkin-cancel"
                className="flex-1 py-2 border border-brand-border text-brand-text-muted font-sans text-sm hover:text-brand-text"
              >
                Cancel
              </button>
              <button
                onClick={submitWalkIn}
                disabled={savingWalkIn}
                data-testid="walkin-save"
                className="flex-1 py-2 bg-brand-gold text-black font-sans font-semibold text-sm hover:bg-brand-gold-light transition-colors disabled:opacity-60"
              >
                {savingWalkIn ? "Saving..." : "Book Walk-In"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarTab;
